import { cn } from '../../lib/utils';
import { chatTheme as th } from '../../theme/chat';

const ACCOUNTING_METHODS = ['Accrual', 'Cash'];

// "Column By" options — time-based granularity OR dimension split
const TIME_OPTIONS = [
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'year', label: 'Yearly' },
];

const DIMENSION_OPTIONS = [
  { value: 'company', label: 'By Company', multiCompanyOnly: true },
  { value: 'department', label: 'By Department' },
  { value: 'class', label: 'By Class' },
  { value: 'customer', label: 'By Customer' },
  { value: 'vendor', label: 'By Vendor' },
  { value: 'location', label: 'By Location' },
];

export default function ReportForm({
  parameters,
  onParameterChange,
  companyCount = 0,
}) {
  const handleDateChange = (index, value) => {
    const range = [...(parameters.date_range || ['2025-01-01', '2025-12-31'])];
    range[index] = value;
    onParameterChange('date_range', range);
  };

  // Derive current "Column By" value from parameters
  const dimensionSplits = parameters.dimension_splits || [];
  const hasSplit = dimensionSplits.length > 0;
  const columnByValue = hasSplit ? `split:${dimensionSplits[0]}` : (parameters.granularity || 'month');

  const handleColumnByChange = (value) => {
    if (value.startsWith('split:')) {
      // Dimension split — set dimension_splits, granularity stays but is ignored by backend
      const dim = value.replace('split:', '');
      onParameterChange('dimension_splits', [dim]);
    } else {
      // Time granularity — clear dimension_splits
      onParameterChange('dimension_splits', []);
      onParameterChange('granularity', value);
    }
  };

  const dateRange = parameters.date_range || ['2025-01-01', '2025-12-31'];

  // Filter dimension options — "company" only when multiple companies
  const availableDimensions = DIMENSION_OPTIONS.filter(
    d => !d.multiCompanyOnly || companyCount > 1
  );

  return (
    <div className={cn('flex flex-wrap items-end gap-3 px-4 py-3 flex-shrink-0', th.form.bg, th.form.border)}>
      {/* Date range */}
      <div className="flex flex-col gap-1">
        <span className={th.form.label}>Date Range</span>
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={dateRange[0]}
            onChange={e => handleDateChange(0, e.target.value)}
            className={th.form.input}
          />
          <span className="text-zinc-400 text-xs">to</span>
          <input
            type="date"
            value={dateRange[1]}
            onChange={e => handleDateChange(1, e.target.value)}
            className={th.form.input}
          />
        </div>
      </div>

      {/* Column By — unified granularity + dimension split */}
      <div className="flex flex-col gap-1">
        <span className={th.form.label}>Column By</span>
        <select
          value={columnByValue}
          onChange={e => handleColumnByChange(e.target.value)}
          className={th.form.select}
        >
          <optgroup label="Time">
            {TIME_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </optgroup>
          <optgroup label="Dimension">
            {availableDimensions.map(o => (
              <option key={o.value} value={`split:${o.value}`}>{o.label}</option>
            ))}
          </optgroup>
        </select>
      </div>

      {/* Accounting method */}
      <div className="flex flex-col gap-1">
        <span className={th.form.label}>Method</span>
        <select
          value={parameters.accounting_method || 'Accrual'}
          onChange={e => onParameterChange('accounting_method', e.target.value)}
          className={th.form.select}
        >
          {ACCOUNTING_METHODS.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
